// HelpSupportPage: Full-page help and support interface
// Transform from modal to dedicated page for better mobile UX

import React, { useState } from 'react'
import { contactService, type ContactFormData } from '@/services/ContactService'
import {
  HelpCircle,
  MessageCircle,
  BookOpen,
  Send,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  Mail,
  FileText,
  Video,
  Search,
  CheckCircle
} from 'lucide-react'

// Use the ContactFormData interface from the service
// interface ContactForm is replaced by ContactFormData from service

const faqs = [
  {
    id: 1,
    question: "How do I upload images to StockTagger.ai?",
    answer: "Simply drag and drop your images onto the upload area or click to browse and select files. We support JPG, PNG, and WebP formats up to 10MB each.",
    category: "Getting Started"
  },
  {
    id: 2,
    question: "What does AI metadata generation include?",
    answer: "Our AI analyzes your images and generates titles, descriptions, keywords, and categories optimized for stock photography platforms like Shutterstock, Adobe Stock, and Getty Images.",
    category: "Features"
  },
  {
    id: 3,
    question: "How many credits do I get with each plan?",
    answer: "Starter: 500 credits/month, Professional: 2000 credits/month, Business: 10,000 credits/month, Enterprise: 50,000+ credits/month. Each image processed costs 1 credit.",
    category: "Billing"
  },
  {
    id: 4,
    question: "Can I export metadata to multiple platforms?",
    answer: "Yes! You can export your metadata in formats optimized for Shutterstock, Adobe Stock, iStock, Getty Images, or generic CSV format.",
    category: "Features"
  },
  {
    id: 5,
    question: "Is my data secure and private?",
    answer: "Absolutely. We use enterprise-grade encryption, secure cloud storage, and never share your images or metadata with third parties. You maintain full ownership of your content.",
    category: "Security"
  },
  {
    id: 6,
    question: "How do I cancel my subscription?",
    answer: "You can cancel anytime through your account settings > Manage Subscription > Billing Portal. Your subscription will remain active until the end of your billing period.",
    category: "Billing"
  }
]

const helpResources = [
  {
    id: 1,
    title: "Getting Started Guide",
    description: "Complete walkthrough for new users",
    type: "guide",
    icon: BookOpen,
    url: "https://docs.stocktagger.ai/getting-started"
  },
  {
    id: 2,
    title: "Video Tutorials",
    description: "Step-by-step video instructions",
    type: "video",
    icon: Video,
    url: "https://www.youtube.com/@StockTaggerAI/tutorials"
  },
  {
    id: 3,
    title: "API Documentation",
    description: "Technical documentation for developers",
    type: "docs",
    icon: FileText,
    url: "https://api.stocktagger.ai/docs"
  },
  {
    id: 4,
    title: "Best Practices",
    description: "Tips for optimizing your workflow",
    type: "guide",
    icon: BookOpen,
    url: "https://docs.stocktagger.ai/best-practices"
  }
]

export function HelpSupportPage() {
  const [activeTab, setActiveTab] = useState<'faq' | 'contact' | 'resources'>('faq')
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [contactForm, setContactForm] = useState<ContactFormData>({
    subject: '',
    category: 'general',
    message: '',
    email: ''
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitSuccess, setSubmitSuccess] = useState(false)

  const tabs = [
    { id: 'faq' as const, name: 'FAQ', icon: HelpCircle },
    { id: 'contact' as const, name: 'Contact Support', icon: MessageCircle },
    { id: 'resources' as const, name: 'Resources', icon: BookOpen }
  ]

  const contactCategories = contactService.getContactCategories()

  const filteredFaqs = faqs.filter(faq =>
    faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
    faq.answer.toLowerCase().includes(searchQuery.toLowerCase()) ||
    faq.category.toLowerCase().includes(searchQuery.toLowerCase())
  )

  const handleContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)

    try {
      const result = await contactService.submitContactForm(contactForm)

      if (result.success) {
        setSubmitSuccess(true)
        setContactForm({
          subject: '',
          category: 'general',
          message: '',
          email: ''
        })
      } else {
        throw new Error(result.error || 'Failed to submit support request')
      }
    } catch (error) {
      console.error('Failed to submit support request:', error)
      // You could add error state management here if needed
      alert(error instanceof Error ? error.message : 'Failed to submit support request')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="space-y-8">
      {/* Tab Navigation */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
        <nav className="flex border-b border-gray-200">
          {tabs.map(tab => {
            const Icon = tab.icon
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 px-6 py-4 border-b-2 font-semibold text-sm transition-colors ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600 bg-blue-50'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Icon className="w-4 h-4" />
                <span>{tab.name}</span>
              </button>
            )
          })}
        </nav>

        <div className="p-6">
          {activeTab === 'faq' && (
            <div className="space-y-8">
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Frequently Asked Questions</h3>
                <p className="text-gray-600 mb-6">
                  Find answers to common questions about StockTagger.ai
                </p>

                {/* Search */}
                <div className="relative">
                  <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search frequently asked questions..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>

              {/* FAQ List */}
              <div className="space-y-4">
                {filteredFaqs.map(faq => (
                  <div key={faq.id} className="border border-gray-200 rounded-xl shadow-sm">
                    <button
                      onClick={() => setExpandedFaq(expandedFaq === faq.id ? null : faq.id)}
                      className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors rounded-xl"
                    >
                      <span className="font-semibold text-gray-900 pr-4">{faq.question}</span>
                      {expandedFaq === faq.id ? (
                        <ChevronDown className="w-5 h-5 text-gray-500 flex-shrink-0" />
                      ) : (
                        <ChevronRight className="w-5 h-5 text-gray-500 flex-shrink-0" />
                      )}
                    </button>

                    {expandedFaq === faq.id && (
                      <div className="px-6 pb-4 border-t border-gray-100">
                        <div className="pt-4">
                          <div className="flex items-center mb-3">
                            <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full font-medium">
                              {faq.category}
                            </span>
                          </div>
                          <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {filteredFaqs.length === 0 && (
                <div className="text-center py-12">
                  <HelpCircle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">No FAQs found</h4>
                  <p className="text-gray-600">Try adjusting your search terms or browse by category.</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'contact' && (
            <div className="space-y-8">
              {submitSuccess ? (
                <div className="text-center py-12">
                  <CheckCircle className="w-20 h-20 text-green-500 mx-auto mb-6" />
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">Message Sent Successfully!</h3>
                  <p className="text-gray-600 mb-6 leading-relaxed">
                    Thanks for reaching out! We'll get back to you within 24 hours.
                  </p>
                  <button
                    onClick={() => {
                      setSubmitSuccess(false)
                      setActiveTab('faq')
                    }}
                    className="text-blue-600 hover:text-blue-700 font-semibold"
                  >
                    Browse FAQ while you wait
                  </button>
                </div>
              ) : (
                <>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">Contact Support</h3>
                    <p className="text-gray-600">
                      Can't find what you're looking for? Send us a message and we'll help you out.
                    </p>
                  </div>

                  <form onSubmit={handleContactSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label htmlFor="contact_email" className="block text-sm font-semibold text-gray-700 mb-2">
                          Email Address
                        </label>
                        <input
                          id="contact_email"
                          type="email"
                          value={contactForm.email}
                          onChange={(e) => setContactForm(prev => ({ ...prev, email: e.target.value }))}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          required
                        />
                      </div>

                      <div>
                        <label htmlFor="contact_category" className="block text-sm font-semibold text-gray-700 mb-2">
                          Category
                        </label>
                        <select
                          id="contact_category"
                          value={contactForm.category}
                          onChange={(e) => setContactForm(prev => ({ ...prev, category: e.target.value }))}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                          {contactCategories.map(category => (
                            <option key={category.value} value={category.value}>
                              {category.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div>
                      <label htmlFor="contact_subject" className="block text-sm font-semibold text-gray-700 mb-2">
                        Subject
                      </label>
                      <input
                        id="contact_subject"
                        type="text"
                        value={contactForm.subject}
                        onChange={(e) => setContactForm(prev => ({ ...prev, subject: e.target.value }))}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Brief description of your issue or question"
                        required
                      />
                    </div>

                    <div>
                      <label htmlFor="contact_message" className="block text-sm font-semibold text-gray-700 mb-2">
                        Message
                      </label>
                      <textarea
                        id="contact_message"
                        rows={6}
                        value={contactForm.message}
                        onChange={(e) => setContactForm(prev => ({ ...prev, message: e.target.value }))}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Please provide as much detail as possible..."
                        required
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2 font-semibold"
                    >
                      <Send className="w-4 h-4" />
                      <span>{isSubmitting ? 'Sending Message...' : 'Send Message'}</span>
                    </button>
                  </form>

                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
                    <div className="flex items-start">
                      <Mail className="w-6 h-6 text-blue-600 mt-1 mr-4 flex-shrink-0" />
                      <div>
                        <h4 className="font-semibold text-blue-800 mb-2">Response Time</h4>
                        <p className="text-blue-700 leading-relaxed">
                          We typically respond within 24 hours during business days.
                          For urgent billing issues, we'll respond within 4 hours.
                        </p>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {activeTab === 'resources' && (
            <div className="space-y-8">
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Help Resources</h3>
                <p className="text-gray-600">
                  Browse our comprehensive documentation and tutorials.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {helpResources.map(resource => {
                  const Icon = resource.icon
                  return (
                    <a
                      key={resource.id}
                      href={resource.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-white border border-gray-200 rounded-xl p-6 hover:bg-gray-50 transition-colors group shadow-sm"
                    >
                      <div className="flex items-center justify-between mb-4">
                        <Icon className="w-8 h-8 text-blue-600" />
                        <ExternalLink className="w-4 h-4 text-gray-400 group-hover:text-gray-600" />
                      </div>
                      <h4 className="font-semibold text-gray-900 mb-2">{resource.title}</h4>
                      <p className="text-gray-600">{resource.description}</p>
                    </a>
                  )
                })}
              </div>

              <div className="bg-gray-50 rounded-xl p-8 border border-gray-200">
                <h4 className="font-semibold text-gray-900 mb-3">Still Need Help?</h4>
                <p className="text-gray-600 mb-6 leading-relaxed">
                  Can't find what you're looking for? Our support team is here to help.
                </p>
                <div className="flex space-x-4">
                  <button
                    onClick={() => setActiveTab('contact')}
                    className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors font-semibold"
                  >
                    Contact Support
                  </button>
                  <button
                    onClick={() => setActiveTab('faq')}
                    className="bg-white border border-gray-300 text-gray-700 px-6 py-3 rounded-lg hover:bg-gray-50 transition-colors font-semibold"
                  >
                    Browse FAQ
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default HelpSupportPage